<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { Button, Input, Label, Select, Checkbox, Card } from "flowbite-svelte";
  import ImageSelector from "$lib/admin/components/media/ImageSelector.svelte";
  import Content from "$lib/admin/components/blocks/blocks/Content/index.svelte";
  import { getBucketLink } from "$lib/common/helpers/image";
  import { project } from "$lib/services/store";
  import Icon from "@iconify/svelte";

  interface ImageData {
    url?: string;
    alt?: string;
    caption?: string;
    border?: boolean;
    hideOnMobile?: boolean;
    hideOnDesktop?: boolean;
    linkUrl?: string;
    openNewWindow?: boolean;
    addNofollow?: boolean;
    captionOutside?: boolean;
    singleCaption?: boolean;
    width?: number;
  }

  interface Props {
    imageBlockType?: string;
    images?: ImageData[];
    content?: string;
  }

  export let props: Props = {};
  export let blockId: string;

  const projectKey = $project.key;
  const IMG_CONTENT_FOLDER = getBucketLink() + "media/";
  const dispatch = createEventDispatcher();
  let selectedImage = 0;

  const getInputId = (prefix: string, idx: number): string => `${prefix}-${blockId}-${idx}`;

  $: {
    if (!props.images) {
      props.images = [{}];
    }
  }
</script>

<div class="mb-4">
  <Label for={getInputId("imageBlockType", 0)}>Image block type</Label>
  <Select id={getInputId("imageBlockType", 0)} bind:value={props.imageBlockType} on:change={() => dispatch("keyup")}>
    <option value="single">Image - Single</option>
    <option value="content">Image - In-content</option>
    <option value="multi">Image - Side-by-side</option>
    <option value="carousel">Image - Carousel</option>
    <option value="link">Image - Link</option>
  </Select>
</div>
{#if props.images.length}
  {#each props.images as image, idx}
    {#if !((props.imageBlockType === "single" || props.imageBlockType === "content") && idx > 0) && !((props.imageBlockType === "multi" || props.imageBlockType === "carousel") && idx != selectedImage)}
      <div class="flex flex-col gap-6 p-4 mb-6 bg-white rounded-lg border border-gray-200 md:flex-row">
        <div class="w-full md:w-1/3">
          <ImageSelector
            imagePath={image.url || ""}
            imageUrl={image.url ? IMG_CONTENT_FOLDER + image.url : ""}
            bucketName="content"
            on:change={() => dispatch("keyup")}
            on:select={(event) => {
              const filePath = event.detail.filePath.replace("cf/images/media/", "");
              image.url = filePath;
              dispatch("keyup");
            }}
            folderPath="cf/images/media/"
            height="250px"
            width="100%"
            placeholderText="Select an image"
            buttonText="Select Image" />
        </div>
        {#if image.url}
          <div class="space-y-4 w-full md:w-2/3">
            <div>
              <Label for={getInputId("alt-text", idx)}>Alt text</Label>
              <Input placeholder="Alternative text" id={getInputId("alt-text", idx)} bind:value={image.alt} on:keyup />
            </div>
            {#if props.imageBlockType === "link"}
              <div class="mt-3">
                <Label for={getInputId("link-url", idx)}>Link URL</Label>
                <Input
                  placeholder="Enter URL"
                  id={getInputId("link-url", idx)}
                  bind:value={image.linkUrl}
                  on:keyup={() => dispatch("keyup")} />
              </div>
              <div class="mt-3">
                <Checkbox
                  id={getInputId("openNewWindow", idx)}
                  bind:checked={image.openNewWindow}
                  on:change={() => dispatch("keyup")}>Open in new window</Checkbox>
              </div>
              <div class="mt-2">
                <Checkbox
                  id={getInputId("addNofollow", idx)}
                  bind:checked={image.addNofollow}
                  on:change={() => dispatch("keyup")}>Add nofollow</Checkbox>
              </div>
            {/if}

            {#if props.imageBlockType === "multi" || props.imageBlockType === "carousel"}
              {#if props.images[0].singleCaption}
                <div class="mt-3">
                  <Label for={getInputId("image-caption", 0)}>Image caption</Label>
                  <Input
                    placeholder="Image caption"
                    id={getInputId("image-caption", 0)}
                    bind:value={props.images[0].caption}
                    on:keyup={() => dispatch("keyup")} />
                </div>
              {:else}
                <div class="mt-3">
                  <Label for={getInputId("image-caption", idx)}>Image caption</Label>
                  <Input
                    placeholder="Image caption"
                    id={getInputId("image-caption", idx)}
                    bind:value={image.caption}
                    on:keyup={() => dispatch("keyup")} />
                </div>
              {/if}
              <div class="mt-2">
                <Checkbox
                  id={getInputId("singleCaption", idx)}
                  bind:checked={props.images[0].singleCaption}
                  on:change={() => dispatch("keyup")}>Single caption</Checkbox>
              </div>
            {:else}
              <div class="mt-3">
                <Label for={getInputId("image-caption", idx)}>Image caption</Label>
                <Input
                  placeholder="Image caption"
                  id={getInputId("image-caption", idx)}
                  bind:value={image.caption}
                  on:keyup={() => dispatch("keyup")} />
              </div>
              <div class="mt-2">
                <Checkbox
                  id={getInputId("captionOutside", idx)}
                  bind:checked={image.captionOutside}
                  on:change={() => dispatch("keyup")}>Caption outside</Checkbox>
              </div>
            {/if}

            <div class="mt-2">
              <Checkbox id={getInputId("border", idx)} bind:checked={image.border} on:change={() => dispatch("keyup")}
                >Image border</Checkbox>
            </div>
            <div class="mt-2">
              <Checkbox
                id={getInputId("hideOnMobile", idx)}
                bind:checked={image.hideOnMobile}
                on:change={() => dispatch("keyup")}>Hide on mobile</Checkbox>
            </div>
            <div class="mt-2">
              <Checkbox
                id={getInputId("hideOnDesktop", idx)}
                bind:checked={image.hideOnDesktop}
                on:change={() => dispatch("keyup")}>Hide on desktop</Checkbox>
            </div>
            {#if props.imageBlockType === "content"}
              <div class="mt-3">
                <Label for={getInputId("width", 0)}>Image width (%)</Label>
                <Input
                  id={getInputId("width", 0)}
                  type="number"
                  bind:value={props.images[0].width}
                  on:change={() => dispatch("keyup")} />
              </div>
            {/if}
          </div>
        {/if}
      </div>
      {#if props.imageBlockType === "content"}
        <Content adminView={true} {props} on:keyup />
      {/if}
      {#if props.imageBlockType === "multi" || props.imageBlockType === "carousel"}
        <div class="flex justify-between items-center p-4 mt-4 bg-white rounded-lg border border-gray-200">
          <div class="flex justify-between items-center w-full">
            <div class="flex items-center space-x-2">
              <div class="text-sm">
                Showing image {selectedImage + 1} of {props.images.length} images
              </div>
              <Button
                size="xs"
                on:click={() => {
                  selectedImage -= 1;
                  if (selectedImage < 0) selectedImage = props.images.length - 1;
                }}>
                <Icon icon="heroicons:chevron-left" />
              </Button>
              <Button
                size="xs"
                on:click={() => {
                  selectedImage += 1;
                  if (selectedImage >= props.images.length) selectedImage = 0;
                }}>
                <Icon icon="heroicons:chevron-right" />
              </Button>
            </div>
            <div class="flex space-x-2">
              <Button
                size="sm"
                color="blue"
                on:click={() => {
                  props.images = [...props.images, {}];
                  selectedImage = props.images.length - 1;
                  dispatch("keyup");
                }}>
                <Icon icon="heroicons:plus" class="mr-1" />
                Add image
              </Button>
              <Button
                size="sm"
                color="red"
                on:click={() => {
                  if (props.images.length > 1) {
                    props.images = props.images.filter((_, i) => i !== selectedImage);
                    if (selectedImage >= props.images.length) {
                      selectedImage = props.images.length - 1;
                    }
                    dispatch("keyup");
                  }
                }}>
                <Icon icon="heroicons:trash" class="mr-1" />
                Remove image
              </Button>
            </div>
          </div>
        </div>
      {/if}
    {/if}
  {/each}
{/if}

<style>
  .multi-meta {
    display: flex;
    align-items: center;
    padding: 0 1rem;
  }
</style>
